import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  forwardRef,
} from "react";
import { createPortal } from "react-dom";
import { TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const MultiLineCreatableSelect = React.memo(
  forwardRef(
    (
      {
        options = [],
        value = "",
        multiline = true,
        selectedValues = [],
        onChange,
        onSelect,
        onBlur,
        onFocus,
        onMenuOpen,
        onMenuClose,
        onBeforeClear,
        clearAriaLabel = "Clear",
        placeholder = "Type or select...",
        menuPortalTarget = null,
        isDisabled = false,
        allowCreate = true,
        isScoring = false,
        containerClasses="",
      },
      ref
    ) => {
      const [input, setInput] = useState(value);
      const [open, setOpen] = useState(false);

      const wrapperRef = useRef(null);
      const dropdownRef = useRef(null);
      console.log('placeholder: ',placeholder);
      console.log('containerClasses: ',containerClasses);
      const [dropdownPosition, setDropdownPosition] =
        useState({
          top: 0,
          left: 0,
          width: 0,
        });

      // =========================
      // Sync external value
      // =========================
      useEffect(() => {
        setInput(value || "");
      }, [value]);

      // =========================
      // Close dropdown on outside click
      // =========================
      useEffect(() => {
        // A disabled cell can't open its menu, so it never needs the global
        // outside-click listener. Skipping it here avoids attaching one document
        // listener per read-only cell (hundreds across a large report).
        if (isDisabled) return;
        const handleClickOutside = (e) => {
          const isOutsideWrapper =
            wrapperRef.current &&
            !wrapperRef.current.contains(e.target);

          const isOutsideDropdown =
            menuPortalTarget &&
            dropdownRef.current &&
            !dropdownRef.current.contains(e.target);

          if (menuPortalTarget) {
            if (
              isOutsideWrapper &&
              isOutsideDropdown
            ) {
              setOpen(false);
              setInput(value || "");
            }
          } else {
            if (isOutsideWrapper) {
              setOpen(false);
              setInput(value || "");
            }
          }
        };

        document.addEventListener(
          "mousedown",
          handleClickOutside
        );

        return () => {
          document.removeEventListener(
            "mousedown",
            handleClickOutside
          );
        };
      }, [menuPortalTarget, value, isDisabled]);

      // =========================
      // Dropdown position
      // =========================
      useEffect(() => {
        if (open) {
          onMenuOpen?.();

          if (
            menuPortalTarget &&
            wrapperRef.current
          ) {
            const rect =
              wrapperRef.current.getBoundingClientRect();

            setDropdownPosition({
              top:
                rect.bottom +
                window.scrollY +
                4,
              left:
                rect.left +
                window.scrollX,
              width: rect.width,
            });
          }
        } else {
          onMenuClose?.();
        }
      }, [
        open,
        onMenuOpen,
        onMenuClose,
        menuPortalTarget,
      ]);

      // Recompute the portaled menu's position whenever `input` changes, not
      // just on open. The field is multiline, so typing the "Create" value grows
      // it and pushes its bottom edge down; without tracking `input` the menu
      // kept the top it had when first opened and floated in the middle of the
      // now-taller field, only snapping into place once a scroll fired the
      // handler below. useLayoutEffect reads the rect after the field has grown
      // but before paint, so it repositions in the same frame with no flicker.
      useLayoutEffect(() => {
        if (!open || !menuPortalTarget || !wrapperRef.current) return;

        const rect = wrapperRef.current.getBoundingClientRect();

        setDropdownPosition((prev) => {
          const next = {
            top: rect.bottom + window.scrollY + 4,
            left: rect.left + window.scrollX,
            width: rect.width,
          };

          if (
            prev.top === next.top &&
            prev.left === next.left &&
            prev.width === next.width
          ) {
            return prev;
          }

          return next;
        });
      }, [open, input, menuPortalTarget]);

      // =========================
      // Update position on scroll
      // =========================
      useEffect(() => {
        if (!open || !menuPortalTarget) return;

        let frameId = null;

        const updatePosition = () => {
          if (!wrapperRef.current) return;

          const rect =
            wrapperRef.current.getBoundingClientRect();

          setDropdownPosition((prev) => {
            const next = {
              top:
                rect.bottom +
                window.scrollY +
                4,
              left:
                rect.left +
                window.scrollX,
              width: rect.width,
            };

            // Prevent unnecessary rerenders
            if (
              prev.top === next.top &&
              prev.left === next.left &&
              prev.width === next.width
            ) {
              return prev;
            }

            return next;
          });

          frameId = null;
        };

        const handleScroll = () => {
          if (frameId) return;

          frameId =
            requestAnimationFrame(updatePosition);
        };

        window.addEventListener(
          "scroll",
          handleScroll,
          true
        );

        window.addEventListener(
          "resize",
          handleScroll
        );

        return () => {
          window.removeEventListener(
            "scroll",
            handleScroll,
            true
          );

          window.removeEventListener(
            "resize",
            handleScroll
          );

          if (frameId) {
            cancelAnimationFrame(frameId);
          }
        };
      }, [open, menuPortalTarget]);

      // =========================
      // Lightweight read-only view
      // =========================
      // A disabled cell can't be opened, focused into, or typed in, so there is
      // no reason to mount a full MUI TextField (outlined input + fieldset +
      // emotion styling) for it. Under virtualization that control was being
      // mounted ~10x per row for every non-edited row, which is what made fast
      // scrolling blank and lag. This static box matches the disabled field's
      // look (grey fill, border, value text, dropdown chevron) at a fraction of
      // the render cost. The instant the row enters edit mode `isDisabled` flips
      // to false and the real interactive control mounts again, so editing is
      // unchanged. Empty cells render no text (an editing prompt is meaningless
      // and misleading on a read-only cell).
      if (isDisabled) {
        const hasValue = input !== "" && input != null;
        return (
          <div
            ref={wrapperRef}
            style={{
              position: "relative",
              width: isScoring ? 100 : 170,
              maxWidth: "100%",
              display: "inline-block",
            }}
          >
            <div
              style={{
                boxSizing: "border-box",
                minHeight: 38,
                width: isScoring ? 100 : 170,
                maxWidth: "100%",
                display: "flex",
                alignItems: "center",
                background: "#f5f5f5",
                border: "1px solid rgba(0, 0, 0, 0.26)",
                borderRadius: 4,
                padding: isScoring ? "6px" : "6px 30px 6px 6px",
                fontSize: 12,
                lineHeight: multiline ? 1.4 : "normal",
                color: "rgba(0, 0, 0, 0.6)",
                cursor: "not-allowed",
              }}
            >
              <span
                style={{
                  display: "block",
                  width: "100%",
                  minWidth: 0,
                  whiteSpace: multiline ? "pre-wrap" : "nowrap",
                  overflow: multiline ? "visible" : "hidden",
                  textOverflow: multiline ? "clip" : "ellipsis",
                  wordBreak: multiline ? "break-word" : "normal",
                  overflowWrap: multiline ? "break-word" : "normal",
                }}
              >
                {hasValue ? input : null}
              </span>
            </div>
            {!isScoring && (
              <KeyboardArrowDownIcon
                aria-hidden="true"
                sx={{
                  position: "absolute",
                  right: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: 18,
                  color: "#999",
                  pointerEvents: "none",
                }}
              />
            )}
          </div>
        );
      }

      // =========================
      // Filtered options
      // Always show selected option
      // =========================
      // useEffect(()=>{
      // console.log ("options",JSON.stringify(options))
       
      // },[options])

      const filteredOptions = options.filter((opt) => {
        if (
          value === opt ||
          selectedValues.includes(opt)
        ) {
          return true;
        }

        return typeof opt === 'string' && typeof input === 'string'
          ? opt.toLowerCase().includes(input.toLowerCase())
          : String(opt).toLowerCase().includes(String(input).toLowerCase());
      });

      const normalizedInput = typeof input === 'string'
        ? input.replace(/\s+/g, "").toLowerCase()
        : String(input).replace(/\s+/g, "").toLowerCase();

      const showCreateOption =
        allowCreate &&
        normalizedInput &&
        !options.some(
          (opt) =>
            typeof opt === 'string'
              ? opt.replace(/\s+/g, "").toLowerCase() === normalizedInput
              : String(opt).replace(/\s+/g, "").toLowerCase() === normalizedInput
        );

      // =========================
      // Handlers
      // =========================
      const handleSelect = (opt) => {
        setInput(opt);
        setOpen(false);

        onChange?.(opt);
        onSelect?.(opt);
      };

      // const handleInputChange = (e) => {
      //   const val = e.target.value;

      //   setInput(val);
      //   setOpen(true);
      // };

      const handleInputChange = (e) => {
        if (!allowCreate) return;
        let val = e.target.value;
        val = val.replace(/\r\n/g, "\n");
        setInput(val);
        setOpen(true);
        onChange?.(val);
      };

      const handleBlur = () => {
        setOpen(false);

        // Restore selected value
        setInput(value || "");

        onBlur?.();
      };

      const handleFocus = (e) => {
        if (!isDisabled) {
          setOpen(true);
        }

        onFocus?.(e);
      };

      const handleClear = (e) => {
        e.stopPropagation();

        if (typeof onBeforeClear === "function" && onBeforeClear() === false) {
          return;
        }

        setInput("");

        onChange?.("");
        onSelect?.("");
      };

      const toggleOpen = () => {
        if (!isDisabled) {
          setOpen((prev) => !prev);
        }
      };

      // =========================
      // DROPDOWN
      // =========================
      const renderDropdown = () => (
        <div
          ref={dropdownRef}
          style={{
            position: "absolute",
            willChange: "transform",
            transform: "translateZ(0)",
            backfaceVisibility: "hidden",
            top: menuPortalTarget
              ? dropdownPosition.top
              : "100%",
            left: menuPortalTarget
              ? dropdownPosition.left
              : 0,
            right: menuPortalTarget
              ? undefined
              : 0,
            width: menuPortalTarget
              ? dropdownPosition.width
              : "100%",
            marginTop: menuPortalTarget
              ? 0
              : 4,
            background: "#fff",
            border:
              "1px solid hsl(0, 0%, 80%)",
            borderRadius: 4,
            maxHeight: 170,
            overflowY: "auto",
            zIndex: 9999,
            boxShadow:
              "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          {filteredOptions.map((opt) => {
            const selected =
              selectedValues.includes(opt) ||
              value === opt;

            return (
              <div
                key={opt}
                onMouseDown={(e) =>
                  e.preventDefault()
                }
                onClick={() => {
                  if (!selected) {
                    handleSelect(opt);
                  }
                }}
                onMouseEnter={(e) => {
                  if (!selected) {
                    e.currentTarget.style.backgroundColor =
                      "#DEEBFF";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!selected) {
                    e.currentTarget.style.backgroundColor =
                      "transparent";
                  }
                }}
                style={{
                  padding: "8px 12px",
                  fontSize: "14px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",

                  backgroundColor: selected
                    ? "#2684FF"
                    : "transparent",

                  color: selected
                    ? "#FFFFFF"
                    : "#333",

                  fontWeight: selected
                    ? 500
                    : 400,

                  cursor: selected
                    ? "default"
                    : "pointer",

                  pointerEvents: "auto",

                  transition:
                    "background-color 0.2s",
                }}
              >
                {opt}
              </div>
            );
          })}

          {filteredOptions.length === 0 &&
            !showCreateOption && (
              <div
                style={{
                  padding: "10px 12px",
                  fontSize: "14px",
                  color: "#999",
                  textAlign: "center",
                  backgroundColor: "#fff",
                }}
              >
                No options
              </div>
            )}
          {showCreateOption && (
            <div
              key="__create__"
              onMouseDown={(e) =>
                e.preventDefault()
              }
              onClick={() => handleSelect(input)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  "#DEEBFF";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  "transparent";
              }}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                fontSize: "14px",
                color: "#1976d2",
                fontWeight: 600,
                borderTop:
                  filteredOptions.length > 0
                    ? "1px solid #f0f0f0"
                    : "none",
                lineHeight: "1.4",
              }}
            >
              {/* Keep the "Create" action label on its own line and show the
                  typed value as a separate block below it. The value can be
                  multiline (e.g. a numbered checkpoint list like "1. ...\n2.
                  ..."); rendering it inline as Create "..." used to push the
                  quote and first item onto the label line and wrap the rest
                  underneath. Splitting them keeps the list readable. Both share
                  the same colour/weight from the parent. */}
              <div style={{ marginBottom: 6 }}>Create</div>
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {input}
              </div>
            </div>
          )}
        </div>
      );

      return (
        <div
          ref={wrapperRef}
          style={{
            position: "relative",
            width: "100%",
          }}
          className={containerClasses || ""}
        >
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <TextField
              disabled={isDisabled}
              inputRef={ref}
              value={input}
              placeholder={placeholder}
              onChange={handleInputChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              inputProps={{
                readOnly: !allowCreate,
                autoComplete: "off"
              }}
              onClick={() => {
                if (!isDisabled) {
                  setOpen(true);
                }
              }}
              multiline={multiline}
              minRows={1}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  alignItems: "flex-center",
                  padding:
                    isScoring ? "6px 6px 6px 6px" : "6px 55px 6px 6px",
                  borderRadius: "4px",
                  backgroundColor: isDisabled
                    ? "#f5f5f5"
                    : "#fff",
                  minHeight: "38px",
                  width: isScoring ? "100px" : "170px",
                  cursor: isDisabled
                    ? "not-allowed"
                    : "pointer",
                },

                "& .MuiOutlinedInput-input": {
                  fontSize: "12px",
                  padding: multiline ? "4px 0" : 0,
                  lineHeight: multiline ? "1.4" : "normal",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  cursor: isDisabled
                    ? "not-allowed"
                    : "text",
                },

                "& .Mui-disabled": {
                  cursor: "not-allowed",
                },
              }}
            />

            {/* Right Controls */}
            {!isScoring && (
            <div
              style={{
                position: "absolute",
                right: 4,
                top: "50%",
                transform:
                  "translateY(-50%)",
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
            >
              {input && !isDisabled && (
                <div
                  role="button"
                  tabIndex={0}
                  aria-label={clearAriaLabel}
                  onMouseDown={(e) =>
                    e.preventDefault()
                  }
                  onClick={handleClear}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleClear(e);
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#ccc",
                    padding: "0 3px",
                    height: "100%",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color =
                      "#aaa";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color =
                      "#ccc";
                  }}
                >
                  <CloseIcon
                    style={{ fontSize: 16 }}
                  />
                </div>
              )}

              <div
                style={{
                  width: "1px",
                  height: "16px",
                  backgroundColor: "#d8d8d8",
                }}
              />

              <div
                onMouseDown={(e) =>
                  e.preventDefault()
                }
                onClick={toggleOpen}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isDisabled
                    ? "not-allowed"
                    : "pointer",
                  color: "#999",
                  height: "100%",
                }}
              >
                <KeyboardArrowDownIcon
                  sx={{
                    fontSize: 18,
                    transition:
                      "transform 0.2s",
                    transform: open
                      ? "rotate(180deg)"
                      : "rotate(0deg)",

                    "&:hover": {
                      color: "#666",
                    },
                  }}
                />
              </div>
            </div>
            )}
          </div>

          {/* {open &&
          (filteredOptions.length > 0 ||
            showCreateOption) &&
          (menuPortalTarget
            ? createPortal(
                renderDropdown(),
                menuPortalTarget
              )
            : renderDropdown())} */}
          {open &&
            (menuPortalTarget
              ? createPortal(
                renderDropdown(),
                menuPortalTarget
              )
              : renderDropdown())}
        </div>
      );
    }
  )
);

export default MultiLineCreatableSelect;